
































import ConfirmActionDialog from "@/components/utility/ConfirmActionDialog.vue";
import { RequestStatusEnum } from "@/lib/enum/request-status-enum";
import { IDialog } from "@/lib/interfaces/dialog.interface";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { handleError } from "@/lib/utility/handleError";
import { $t } from "@/lib/utility/t";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { InvoiceParty } from "@/models/invoice-party.entity";
import { IReference } from "@/models/reference.entity";
import { IRepairEstimate, RepairEstimate } from "@/models/repair-estimate.entity";
import { IReport } from "@/models/report.entity";
import { ISharedContent } from "@/models/shared-content.entity";
import { UserModule } from "@/store/modules/me-user.store";
import { mixins } from "vue-class-component";
import { Component, Prop, Ref, Watch } from "vue-property-decorator";
import RepairEstimateCreateStepper, { RepairEstimateSteps } from "./RepairEstimateCreateStepper.vue";

@Component({
  components: {
    RepairEstimateCreateStepper,
    ConfirmActionDialog
  },
  filters: {
    simpleDoubleDigitDate
  }
})
export default class RepairEstimateCreateRequestDialog extends mixins(PermissionMixin) implements IDialog {
  readonly RepairEstimateSteps = RepairEstimateSteps;

  @Ref("requestRepairEstimateStepper")
  requestRepairEstimateStepper!: RepairEstimateCreateStepper;

  @Prop()
  partnerId!: string;

  @Prop()
  hideButton!: boolean;

  @Prop({ default: () => [] })
  refs!: IReference[];

  @Prop({ default: false })
  hideRefsSelect!: boolean;

  @Prop()
  sellerCompanyId?: string;

  @Prop()
  report?: IReport;

  isDialogActive = false;

  isLoading = false;

  requestsLoading = false;

  step = RepairEstimateSteps.SELLER;

  value: IRepairEstimate = new RepairEstimate({
    partnerId: this.partnerId,
    seller: new InvoiceParty(),
    refs: this.refs
  });

  get steps() {
    return new Map([
      [
        RepairEstimateSteps.SELLER,
        {
          title: this.$t("repairEstimate.request"),
          backText: $t("close"),
          nextText: $t("next"),
          loading: false,
          disabled: !this.value?.seller?.ref?.refId,
          back: () => {
            this.close();
          },
          next: () => {
            this.step = RepairEstimateSteps.EXPIRATION;
          }
        }
      ],
      [
        RepairEstimateSteps.EXPIRATION,
        {
          title: this.$t("repairEstimate.untilWhen"),
          subtitle: this.$t("repairEstimate.untilWhenHint"),
          backText: $t("back"),
          nextText: $t("next"),
          loading: this.requestsLoading,
          back: () => {
            this.step = RepairEstimateSteps.SELLER;
          },
          next: async () => {
            this.requestsLoading = true;
            await this.requestRepairEstimateStepper?.createRequests();
            this.requestsLoading = false;
          }
        }
      ],
      [
        RepairEstimateSteps.SEND_MAIL,
        {
          title: this.$t("repairEstimate.sendMail"),
          backText: $t("close"),
          nextText: $t("repairEstimate.sendMail"),
          loading: false,
          back: () => {
            this.close();
          },
          next: async () => {
            try {
              await this.requestRepairEstimateStepper?.templateCard.send();
              await this.value.updatePartial({
                status: RequestStatusEnum.REQUESTED
              });
              await this.value.updateStatusLog();
              this.step = RepairEstimateSteps.SUCCESS;
            } catch (e) {
              handleError(e);
            }
          }
        }
      ],
      [
        RepairEstimateSteps.SUCCESS,
        {
          title: this.$t("repairEstimate.success"),
          backText: $t("close"),
          nextText: $t("common.verbs.view"),
          loading: false,
          back: () => this.close(),
          next: () => this.viewCreated()
        }
      ]
    ]);
  }

  get currentStep() {
    return this.steps.get(this.step);
  }

  get user() {
    return UserModule.user;
  }

  open() {
    const date = new Date();
    date.setDate(date.getDate() + 7);

    const refs = [...this.refs];

    this.value = new RepairEstimate({
      partnerId: this.partnerId,
      seller: new InvoiceParty(),
      refs: refs,
      date: date.toISOString(),
      assignees: this.report?.assignees ?? this.user?._id ? [this.user._id] : [],
      reportId: this.report?.id
    }).setDefaultLineItems();
    if (this.requestRepairEstimateStepper) {
      this.value.seller = new InvoiceParty();
      this.requestRepairEstimateStepper.setSellerFromCompanyId();
    }
    this.step = RepairEstimateSteps.SELLER;
    this.isDialogActive = true;
    this.onReportIdChange();
  }

  @Watch("value.reportId")
  async onReportIdChange() {
    await this.value.fetchReportAndVehicle().catch(this.$log.error);
  }

  async openShareExisting(repairEstimate: IRepairEstimate, sharedContent?: ISharedContent) {
    this.open();

    this.$nextTick(() => {
      if (this.requestRepairEstimateStepper) {
        this.value = repairEstimate;
        this.requestRepairEstimateStepper.stepLocal = RepairEstimateSteps.EXPIRATION;
        if (sharedContent) {
          this.requestRepairEstimateStepper.sharedContent = sharedContent;
          this.requestRepairEstimateStepper.stepLocal = RepairEstimateSteps.SEND_MAIL;
        }
      }
    });
  }

  close() {
    this.step = RepairEstimateSteps.SELLER;
    this.isLoading = false;
    this.value = new RepairEstimate({
      partnerId: this.partnerId,
      seller: new InvoiceParty(),
      refs: this.refs
    }).setDefaultLineItems();
    this.isDialogActive = false;
  }

  viewCreated() {
    this.value.goTo(this.$router).detail();
  }
}
